import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Save, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    booking_expiry_minutes: '60',
    enable_member_pricing: false,
    member_discount_type: 'percent',
    member_discount_value: '0'
  });
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (!token || !userStr) {
      navigate('/admin/login');
      return;
    }
    
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin') {
        navigate('/admin/login');
        return;
      }
    } catch (error) {
      navigate('/admin/login');
      return;
    }
    
    loadSettings();
  }, [navigate]);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings/public');
      const settings = response.data;
      if (settings && Object.keys(settings).length > 0) {
        setFormData({
          booking_expiry_minutes: String(settings.booking_expiry_minutes || '60'),
          enable_member_pricing: String(settings.enable_member_pricing || '').toLowerCase() === 'true' || String(settings.enable_member_pricing || '') === '1',
          member_discount_type: String(settings.member_discount_type || 'percent'),
          member_discount_value: String(settings.member_discount_value || '0')
        });
        try {
          const raw = settings.expense_categories;
          const parsed = raw ? JSON.parse(raw) : [];
          setExpenseCategories(Array.isArray(parsed) ? parsed.map((v) => String(v || '').trim()).filter(Boolean) : []);
        } catch {
          setExpenseCategories([]);
        }
      }
    } catch (error) {
      // Ignore 404 on first load or network errors silently for now, or toast
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const addCategory = () => {
    const v = String(newCategory || '').trim();
    if (!v) return;
    const exists = expenseCategories.some((c) => String(c).toLowerCase() === v.toLowerCase());
    if (exists) {
      toast.error('Kategori sudah ada');
      return;
    }
    setExpenseCategories([...expenseCategories, v]);
    setNewCategory('');
  };

  const removeCategory = (name) => {
    setExpenseCategories(expenseCategories.filter((c) => c !== name));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const settingsArray = [
        { key: 'booking_expiry_minutes', value: String(formData.booking_expiry_minutes || '60') },
        { key: 'expense_categories', value: JSON.stringify(expenseCategories) },
        { key: 'enable_member_pricing', value: formData.enable_member_pricing ? 'true' : 'false' },
        { key: 'member_discount_type', value: String(formData.member_discount_type || 'percent') },
        { key: 'member_discount_value', value: String(formData.member_discount_value || '0') },
      ];

      await api.post('/settings', settingsArray);
      toast.success('Pengaturan berhasil disimpan');
      
      // Force reload to apply changes if needed (or we can use context later)
      // window.location.reload();
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan');
    }
  };

  const testFcm = async () => {
    try {
      const res = await api.post('/admin/test-fcm');
      const data = res?.data || {};
      if (data.enabled === false) {
        toast.error(`FCM tidak aktif: ${data.credentials_error || data.reason || 'tidak diketahui'}`);
        return;
      }
      if (data.ok) {
        toast.success(`Test FCM OK (project: ${data.project_id || '-'})`);
      } else {
        toast.error(`Test FCM gagal (${data.status_code || '-'})`);
      }
    } catch (error) {
      toast.error('Gagal test FCM');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <Link to="/admin/dashboard" className="text-zinc-400 hover:text-white transition-colors">
                <ArrowLeft className="w-6 h-6" />
              </Link>
              <span className="text-xl font-heading font-bold italic text-white uppercase">Pengaturan Website</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-zinc-900 border border-zinc-800 rounded-lg p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Durasi Expire Booking (menit)
              </label>
              <input
                type="number"
                min="5"
                max="1440"
                value={formData.booking_expiry_minutes}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    booking_expiry_minutes: e.target.value.replace(/[^0-9]/g, '')
                  })
                }
                className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary"
                placeholder="60"
              />
              <p className="mt-1 text-xs text-zinc-500">
                Default 60 menit. Minimal 5, maksimal 1440 (1 hari). Booking pending akan otomatis expire setelah durasi ini.
              </p>
            </div>

            <div className="pt-6 border-t border-zinc-800">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div>
                  <div className="text-white font-semibold uppercase tracking-wide">Kategori Pengeluaran</div>
                  <div className="text-xs text-zinc-500">Kategori akan muncul sebagai pilihan di menu pengeluaran.</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="flex-1 bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary"
                  placeholder="Tambah kategori (contoh: Operasional)"
                />
                <Button
                  type="button"
                  onClick={addCategory}
                  className="bg-primary text-black hover:bg-primary/90 font-bold"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Tambah
                </Button>
              </div>

              {expenseCategories.length === 0 ? (
                <div className="text-sm text-zinc-500">Belum ada kategori.</div>
              ) : (
                <div className="space-y-2">
                  {expenseCategories.map((c) => (
                    <div key={c} className="flex items-center justify-between bg-black border border-zinc-800 rounded-md px-4 py-2">
                      <div className="text-white font-medium">{c}</div>
                      <button
                        type="button"
                        onClick={() => removeCategory(c)}
                        className="text-red-400 hover:text-red-300 font-bold uppercase text-xs inline-flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Hapus
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-6 border-t border-zinc-800">
              <div className="text-white font-semibold uppercase tracking-wide mb-1">Harga Member</div>
              <div className="text-xs text-zinc-500 mb-4">Atur diskon otomatis untuk customer yang berstatus member.</div>

              <label className="flex items-center gap-3 mb-4">
                <input
                  type="checkbox"
                  checked={!!formData.enable_member_pricing}
                  onChange={(e) => setFormData({ ...formData, enable_member_pricing: e.target.checked })}
                  className="h-4 w-4 accent-primary"
                />
                <span className="text-sm text-white font-medium">Aktifkan pricing member</span>
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Tipe Diskon</label>
                  <select
                    value={formData.member_discount_type}
                    onChange={(e) => setFormData({ ...formData, member_discount_type: e.target.value })}
                    className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary"
                    disabled={!formData.enable_member_pricing}
                  >
                    <option value="percent">Persen (%)</option>
                    <option value="fixed">Nominal (Rp)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">Nilai Diskon</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.member_discount_value}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        member_discount_value: e.target.value.replace(/[^0-9.]/g, '')
                      })
                    }
                    className="w-full bg-black border border-zinc-800 rounded-md px-4 py-2 text-white focus:outline-none focus:border-primary"
                    placeholder="0"
                    disabled={!formData.enable_member_pricing}
                  />
                  <p className="mt-1 text-xs text-zinc-500">
                    Untuk persen: 0-100. Untuk nominal: nilai Rupiah.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-zinc-800">
              <div className="text-white font-semibold uppercase tracking-wide mb-1">Notifikasi</div>
              <div className="text-xs text-zinc-500 mb-4">Tes kirim notifikasi FCM dari backend.</div>
              <Button type="button" onClick={testFcm} className="bg-primary text-black hover:bg-primary/90 font-bold">
                Test FCM
              </Button>
            </div>

            <div className="pt-6 border-t border-zinc-800">
              <Button type="submit" className="w-full bg-primary text-black hover:bg-primary/90 font-bold">
                <Save className="w-4 h-4 mr-2" />
                Simpan Perubahan
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminSettings;
